import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    // Students Golf: in the bag there's no product card or hotspot around the quick add, so it carries its own address
    return productLink?.href || this.dataset.productUrl || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Students Golf: the showroom's quick add layout on every screen size (see sgShapeQuickAdd below)
    sgShapeQuickAdd(productGrid, this.productPageUrl, this.dataset.productTitle || '');

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/**
 * Students Golf: lays out quick add like the showroom's: a small photo with the name and "Colour · price", the colour
 * pills, the sizes, the fit, "Notify me" / "Full details" links, and Add to bag / Buy now pinned at the bottom (styles:
 * assets/syllabus.css.liquid, "Quick add like the showroom"). Only the size picker re-renders on a size tap, so moving the
 * pieces is safe.
 * @param {Element} grid - The product's [data-product-grid-content] element (a detached clone)
 * @param {string} url - The product page URL
 * @param {string} title - The product title
 */
function sgShapeQuickAdd(grid, url, title) {
  const q = (/** @type {string} */ sel) => grid.querySelector(sel);
  const form = q('product-form-component');
  const picker = q('variant-picker');
  const price = q('product-price');
  const siblings = q('.product-siblings');
  const img = q('.product-information__media img');
  const name = title || q('.view-product-title a')?.textContent?.trim() || '';
  const colour = siblings?.querySelector('.product-siblings__current')?.textContent?.trim() || '';
  const fit = picker?.querySelector('.variant-option__fit')?.textContent?.trim() || '';
  const soldOutSize = Boolean(picker?.querySelector('[data-option-available="false"]'));
  const el = (/** @type {string} */ tag, /** @type {string} */ cls) => {
    const node = document.createElement(tag);
    node.className = cls;
    return node;
  };

  const body = el('div', 'sg-qa__body');
  const prod = el('div', 'sg-qa__prod');
  const thumb = /** @type {HTMLAnchorElement} */ (el('a', 'sg-qa__thumb'));
  thumb.href = url;
  thumb.tabIndex = -1;
  thumb.setAttribute('aria-hidden', 'true');
  const src = img?.getAttribute('src');
  if (src) {
    const photo = /** @type {HTMLImageElement} */ (el('img', ''));
    photo.src = src.replace(/([?&])width=\d+/, '$1width=180');
    photo.alt = '';
    thumb.append(photo);
  }
  const info = el('div', 'sg-qa__info');
  const link = /** @type {HTMLAnchorElement} */ (el('a', 'sg-qa__name'));
  link.href = url;
  link.textContent = name;
  const line = el('p', 'sg-qa__line');
  if (colour) line.append(colour, el('span', 'sg-qa__dot'));
  if (price) line.append(price);
  info.append(link, line);
  prod.append(thumb, info);
  body.append(prod);
  const label = siblings?.querySelector('.product-siblings__label')?.firstChild;
  if (label?.nodeType === Node.TEXT_NODE) label.textContent = 'Color';
  if (siblings) body.append(siblings);
  if (picker) body.append(picker);
  if (fit) {
    const fitLine = el('p', 'sg-qa__fit');
    fitLine.textContent = /\bfit\b/i.test(fit) ? fit : `Fit: ${fit}`;
    body.append(fitLine);
  }
  const links = el('p', 'sg-qa__links');
  if (soldOutSize) {
    const notify = /** @type {HTMLAnchorElement} */ (el('a', ''));
    notify.href = url;
    notify.textContent = 'Notify me when a sold-out size is back';
    links.append(notify, el('span', 'sg-qa__dot'));
  }
  const more = /** @type {HTMLAnchorElement} */ (el('a', ''));
  more.href = url;
  more.textContent = 'Full details';
  links.append(more);
  body.append(links);

  q('.product-information__media')?.remove();
  q('.product-details')?.remove();
  grid.append(body);
  if (form) {
    const foot = el('div', 'sg-qa__foot');
    foot.append(form);
    grid.append(foot);
  }
}

// Students Golf: a colour pill inside quick add switches the colour in place (each colour is its own product), like
// the showroom, instead of leaving for that product's page
/** @type {Map<string, Promise<Element | null>>} */
const sgQuickAddColours = new Map();
const sgFetchGrid = (/** @type {string} */ url) => {
  if (!sgQuickAddColours.has(url)) {
    sgQuickAddColours.set(
      url,
      fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((t) => new DOMParser().parseFromString(t, 'text/html').querySelector('[data-product-grid-content]'))
        .catch(() => null)
    );
  }
  return sgQuickAddColours.get(url);
};
document.addEventListener('pointerover', (e) => {
  const a = e.target instanceof Element && e.target.closest('#quick-add-modal-content .product-siblings__swatch:not([aria-current])');
  if (a instanceof HTMLAnchorElement) sgFetchGrid(a.href);
});
document.addEventListener('click', async (e) => {
  const a = e.target instanceof Element && e.target.closest('#quick-add-modal-content .product-siblings__swatch');
  if (!(a instanceof HTMLAnchorElement) || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  if (a.hasAttribute('aria-current')) return;
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;
  const wanted = sgCheckedOptions(modalContent);
  modalContent.classList.add('sg-qa--loading');
  const source = await sgFetchGrid(a.href);
  modalContent.classList.remove('sg-qa--loading');
  if (!source) {
    window.location.href = a.href;
    return;
  }
  const grid = /** @type {Element} */ (source.cloneNode(true));
  sgShapeQuickAdd(grid, a.href, '');
  modalContent.setAttribute('view-event-payload', grid.getAttribute('view-event-payload') || '');
  // A fresh picker and form, not a morph (review 2026-10-29): morph keeps the old colour's data-current-checked
  // markers, so the sizes showed as unlabelled dark circles with none chosen while the form held the new colour's
  // first size, and Add put Small in the bag for a shopper who had picked Medium.
  modalContent.replaceChildren(...grid.childNodes);
  sgCarryOptions(modalContent, wanted);
});

/**
 * The quick add's chosen size (and any other option) as name and value, read before a colour switch.
 * @param {Element} root
 * @returns {{ name: string, value: string }[]}
 */
function sgCheckedOptions(root) {
  return Array.from(root.querySelectorAll('variant-picker input[type="radio"]:checked'), (input) => ({
    name: /** @type {HTMLInputElement} */ (input).dataset.optionName || '',
    value: /** @type {HTMLInputElement} */ (input).value,
  })).filter((option) => option.name);
}

/**
 * Keeps the shopper's size on the new colour. Choosing it goes through the picker's own change handling, which
 * fetches that variant and updates the price, the form's variant and the Add button. When the new colour doesn't
 * have that size in stock, no size is chosen and Add and Buy now wait until the shopper picks one.
 * @param {Element} root
 * @param {{ name: string, value: string }[]} wanted
 */
function sgCarryOptions(root, wanted) {
  const picker = root.querySelector('variant-picker');
  if (!picker) return;
  for (const { name, value } of wanted) {
    const radios = /** @type {HTMLInputElement[]} */ (
      Array.from(picker.querySelectorAll('input[type="radio"]')).filter(
        (input) => input instanceof HTMLInputElement && input.dataset.optionName === name
      )
    );
    if (!radios.length) continue;
    const match = radios.find((input) => input.value === value);
    if (match && match.dataset.optionAvailable !== 'false') {
      if (!match.checked) {
        match.checked = true;
        match.dispatchEvent(new Event('change', { bubbles: true }));
      }
      continue;
    }
    for (const input of radios) {
      input.checked = false;
      input.removeAttribute('checked');
      input.dataset.currentChecked = 'false';
      input.dataset.previousChecked = 'false';
    }
    const foot = root.querySelector('.sg-qa__foot');
    const held = foot ? Array.from(foot.querySelectorAll('button')).filter((button) => !button.disabled) : [];
    for (const button of held) button.disabled = true;
    const colour = root.querySelector('.product-siblings__current')?.textContent?.trim();
    const note = document.createElement('p');
    note.className = 'sg-qa__note';
    note.setAttribute('role', 'status');
    note.textContent = `${value} isn't available${colour ? ` in ${colour}` : ''}. Choose a size.`;
    picker.after(note);
    picker.addEventListener(
      'change',
      () => {
        for (const button of held) button.disabled = false;
        note.remove();
      },
      { once: true }
    );
  }
}
